import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCroData, fetchCroWeekly, fetchGoogleAds } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CroSettingsDialog from "@/pages/client-tabs/CroSettingsDialog";
import MetaAdsSection from "@/pages/client-tabs/MetaAdsSection";
import AnalyticsSection from "@/pages/client-tabs/AnalyticsSection";
import GoogleAdsFullSection from "@/pages/client-tabs/GoogleAdsFullSection";
import SearchConsoleSection from "@/pages/client-tabs/SearchConsoleSection";
import SearchTermsDualSection from "@/pages/client-tabs/SearchTermsDualSection";
import MonthlyMetricsTable from "@/pages/client-tabs/MonthlyMetricsTable";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis } from "recharts";
import {
  MousePointer, Settings, BarChart3, Search as SearchIcon, Lightbulb, Target, DollarSign, Eye, MousePointerClick, TrendingUp, Activity, Globe2, Layers, AlertCircle, ArrowUpRight, Sparkles, Ban, CheckCircle2, Calendar, RefreshCw, FileText, Plug, LineChart as LineChartIcon, Table as TableIcon,
} from "lucide-react";

const COUNTRY_FLAGS = { AR: "🇦🇷", MX: "🇲🇽", ES: "🇪🇸", CL: "🇨🇱", CO: "🇨🇴", UY: "🇺🇾", BR: "🇧🇷", PE: "🇵🇪", US: "🇺🇸" };

export default function CroTab({ clientId }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["cro-data", clientId],
    queryFn: () => fetchCroData(clientId),
  });

  if (isLoading || !data) {
    return <div className="space-y-3"><div className="h-10 rounded-md skeleton" /><div className="h-72 rounded-md skeleton" /></div>;
  }

  const isConfigured = !!data.config;

  return (
    <div data-testid="cro-tab">
      {!isConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2.5 mb-4 flex items-start gap-2 text-xs text-amber-900">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span><span className="font-semibold">CRO sin compromiso definido todavía.</span> Conectá Google Ads / Meta Ads vía Metricool o agregá un report de Looker Studio para ver Compromiso, Performance y Términos. La <span className="font-semibold">Bitácora semanal</span> (Acciones) ya está disponible si el cliente está en la planilla.</span>
        </div>
      )}
      <Tabs defaultValue={isConfigured ? "tablero" : "actions"} className="w-full">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md inline-flex flex-wrap gap-1">
            <TabsTrigger value="tablero" data-testid="cro-tab-tablero" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <TableIcon className="h-3.5 w-3.5" /> Tablero mensual
            </TabsTrigger>
            <TabsTrigger value="performance" data-testid="cro-tab-performance" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" /> Performance
            </TabsTrigger>
            <TabsTrigger value="terms" data-testid="cro-tab-terms" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <SearchIcon className="h-3.5 w-3.5" /> Términos de búsqueda
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="cro-tab-analytics" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <LineChartIcon className="h-3.5 w-3.5" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="search-console" data-testid="cro-tab-search-console" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <SearchIcon className="h-3.5 w-3.5" /> Search Console
            </TabsTrigger>
            <TabsTrigger value="actions" data-testid="cro-tab-actions" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" /> Acciones
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="cro-tab-config" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
              <Settings className="h-3.5 w-3.5" /> Compromiso CRO
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="cro-config-btn">
            <Settings className="h-3.5 w-3.5 mr-1.5" /> Configurar compromiso
          </Button>
        </div>

        <TabsContent value="config" className="mt-6">{data.config ? <ConfigSection c={data.config} /> : <EmptyTab msg="Compromiso pendiente." />}</TabsContent>
        <TabsContent value="tablero" className="mt-6">
          <MonthlyMetricsTable
            clientId={clientId}
            endpoint={`/clients/${clientId}/cro/monthly-metrics`}
            title="Tablero mensual CRO"
            subtitle="Paid Ads · SEO · AEO/IA · Bing · Palabras clave — mes a mes. Solo meses cerrados."
            sourceOptions={data.config?.data_sources || null}
          />
        </TabsContent>
        <TabsContent value="performance" className="mt-6"><PerformanceSection clientId={clientId} customerId={data.config?.google_ads_customer_id || ""} /></TabsContent>
        <TabsContent value="terms" className="mt-6"><SearchTermsDualSection customerId={data.config?.google_ads_customer_id || ""} gscSiteUrl={data.config?.gsc_site_url || ""} /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AnalyticsSection propertyId={data.config?.ga4_property_id || ""} /></TabsContent>
        <TabsContent value="search-console" className="mt-6"><SearchConsoleSection clientId={clientId} gscSiteUrl={data.config?.gsc_site_url || ""} /></TabsContent>
        <TabsContent value="actions" className="mt-6"><ActionsSection clientId={clientId} /></TabsContent>
      </Tabs>

      <CroSettingsDialog clientId={clientId} croData={data} open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}

/* ============== Configuración ============== */
function ConfigSection({ c }) {
  return (
    <div className="space-y-5" data-testid="cro-config">
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-2 mb-4 flex-wrap">
          <Settings className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-slate-900">Compromiso del período</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{c.report_period_start} → {c.report_period_end}</p>
          </div>
          {c.monthly_budget_usd > 0 && (
            <span className="text-[11px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-sm font-semibold">
              Budget total: ${c.monthly_budget_usd.toLocaleString()} /mes
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <Field label="Países objetivo">
            <div className="flex flex-wrap gap-1.5">
              {(c.countries || []).map((cc) => (
                <span key={cc} className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm font-medium text-slate-700 inline-flex items-center gap-1">
                  <span>{COUNTRY_FLAGS[cc] || "🌐"}</span> {cc}
                </span>
              ))}
            </div>
          </Field>
          <Field label="Redes activas">
            <div className="flex flex-wrap gap-1.5">
              {(c.networks || []).map((n) => (
                <span key={n} className="text-[11px] bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-sm font-medium">{n}</span>
              ))}
            </div>
          </Field>
          <Field label="Objetivos">
            <div className="flex flex-wrap gap-1.5">
              {(c.objectives || []).map((o) => (
                <span key={o} className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-sm font-medium">{o}</span>
              ))}
            </div>
          </Field>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Plataformas conectadas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          {(c.platforms || []).map((p) => (
            <div key={p.name} className={`border rounded-md p-3 ${p.status === "active" ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-medium text-sm text-slate-900">{p.name}</div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${p.status === "active" ? "text-emerald-700" : "text-slate-500"}`}>{p.status === "active" ? "Activa" : "Pausada"}</span>
              </div>
              <div className="text-[10px] text-slate-500 font-mono">Linkeada vía: {p.linked_via}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display text-sm font-semibold text-slate-900">Campañas activas · budget por campaña</h3>
          {(() => {
            const sum = (c.active_campaigns || []).reduce((a, x) => a + (Number(x.monthly_budget_usd) || 0), 0);
            if (sum === 0) return null;
            return <span className="text-[10px] font-mono text-slate-600">Σ asignado: <span className="font-semibold text-slate-900">${sum.toLocaleString()}</span></span>;
          })()}
        </div>
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2 font-semibold">Campaña</th>
                <th className="px-2 py-2 font-semibold">Plataforma</th>
                <th className="px-2 py-2 font-semibold">Objetivo</th>
                <th className="px-2 py-2 font-semibold text-right">Budget mensual</th>
                <th className="px-2 py-2 font-semibold w-24">% del total</th>
                <th className="px-2 py-2 font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(c.active_campaigns || []).map((cmp) => {
                const monthly = cmp.monthly_budget_usd ?? (cmp.daily_budget_usd ? cmp.daily_budget_usd * 30 : 0);
                const total = c.monthly_budget_usd || 0;
                const pct = total > 0 ? Math.round((monthly / total) * 100) : 0;
                return (
                  <tr key={cmp.name} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800">{cmp.name}</td>
                    <td className="px-2 py-2 text-slate-600">{cmp.platform}</td>
                    <td className="px-2 py-2"><span className="text-[10px] bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded-sm font-medium">{cmp.objective}</span></td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900">${monthly.toLocaleString()}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex-1 min-w-[40px]">
                          <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className="font-mono text-[10px] tabular-nums text-slate-600 w-9 text-right">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-2"><span className={`text-[10px] font-semibold uppercase tracking-wider ${cmp.status === "active" ? "text-emerald-700" : "text-slate-500"}`}>{cmp.status === "active" ? "Activa" : "Pausada"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {c.notes && (
          <p className="mt-4 text-xs text-slate-600 leading-relaxed italic border-l-2 border-slate-200 pl-3">{c.notes}</p>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}

/* ============== Performance · Google Ads (via Metricool) ============== */

const PERIOD_PRESETS = [
  { id: "month", label: "Mes" },
  { id: "months", label: "Rango de meses" },
  { id: "dates", label: "Rango de fechas" },
];

const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function periodToDates(preset, monthSingle, monthFrom, monthTo, customFrom, customTo) {
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "month") {
    if (!monthSingle) return null;
    const [y, m] = monthSingle.split("-").map(Number);
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0);
    return { from: fmt(from), to: fmt(to) };
  }
  if (preset === "months") {
    if (!monthFrom || !monthTo) return null;
    const [yF, mF] = monthFrom.split("-").map(Number);
    const [yT, mT] = monthTo.split("-").map(Number);
    const from = new Date(yF, mF - 1, 1);
    const to = new Date(yT, mT, 0);
    return { from: fmt(from), to: fmt(to) };
  }
  if (preset === "dates") {
    return { from: customFrom, to: customTo };
  }
  return null;
}

function buildMonthOptions(n = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    out.push({ value: `${d.getFullYear()}-${m}`, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

function PerformanceSection({ clientId, customerId }) {
  const [preset, setPreset] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const monthOptions = useMemo(() => buildMonthOptions(24), []);
  const defaultMonth = monthOptions[0]?.value || "";
  const [monthSingle, setMonthSingle] = useState(defaultMonth);
  const [monthFrom, setMonthFrom] = useState(defaultMonth);
  const [monthTo, setMonthTo] = useState(defaultMonth);
  const [network, setNetwork] = useState("adwords");

  const range = useMemo(
    () => periodToDates(preset, monthSingle, monthFrom, monthTo, customFrom, customTo),
    [preset, monthSingle, monthFrom, monthTo, customFrom, customTo]
  );

  const enabled = !!(range?.from && range?.to);
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["google-ads", clientId, range?.from, range?.to, network],
    queryFn: () => fetchGoogleAds(clientId, range.from, range.to, network),
    // Only call Metricool fallback for non-Google platforms. Google Ads uses the rich direct API below.
    enabled: enabled && network !== "adwords",
    staleTime: 2 * 60 * 1000,
  });

  const platformLabel = network === "adwords" ? "Google Ads" : network === "facebookAds" ? "Meta Ads" : "LinkedIn Ads";
  const monthOptionsAsc = useMemo(() => [...monthOptions].reverse(), [monthOptions]);

  // When Meta Ads is selected, render the rich MetaAdsSection (its own range selector + sub-tabs).
  if (network === "facebookAds") {
    return (
      <div className="space-y-4" data-testid="cro-performance">
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Plataforma de ads</div>
          <div className="flex gap-1">
            {[
              { id: "adwords", label: "Google Ads" },
              { id: "facebookAds", label: "Meta Ads" },
              { id: "linkedinAds", label: "LinkedIn Ads" },
            ].map((n) => (
              <button
                key={n.id}
                onClick={() => setNetwork(n.id)}
                data-testid={`cro-perf-net-${n.id}`}
                className={`text-[11px] px-2.5 py-1 rounded-sm font-medium border transition-colors ${
                  network === n.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
        </div>
        <MetaAdsSection clientId={clientId} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cro-performance">
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Plataforma de ads</div>
            <div className="flex gap-1">
              {[
                { id: "adwords", label: "Google Ads" },
                { id: "facebookAds", label: "Meta Ads" },
                { id: "linkedinAds", label: "LinkedIn Ads" },
              ].map((n) => (
                <button
                  key={n.id}
                  onClick={() => setNetwork(n.id)}
                  data-testid={`cro-perf-net-${n.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-sm font-medium border transition-colors ${
                    network === n.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Período</div>
            <div className="flex flex-wrap gap-1">
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`cro-perf-preset-${p.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-sm font-medium border transition-colors ${
                    preset === p.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={!enabled || isFetching}
            className="text-[11px] font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-400 px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40 h-7"
            data-testid="cro-perf-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} /> Refrescar
          </button>
        </div>

        {preset === "month" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100" data-testid="cro-perf-month-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Mes</div>
              <select value={monthSingle} onChange={(e) => setMonthSingle(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="cro-perf-month-single">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-1 mb-px">
              <button onClick={() => setMonthSingle(monthOptions[0].value)} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Mes actual</button>
              <button onClick={() => monthOptions[1] && setMonthSingle(monthOptions[1].value)} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Mes anterior</button>
            </div>
          </div>
        )}

        {preset === "months" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100 flex-wrap" data-testid="cro-perf-months-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Desde mes</div>
              <select value={monthFrom} onChange={(e) => setMonthFrom(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="cro-perf-month-from">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Hasta mes</div>
              <select value={monthTo} onChange={(e) => setMonthTo(e.target.value)} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white min-w-[140px]" data-testid="cro-perf-month-to">
                {monthOptionsAsc.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-1 mb-px">
              <button onClick={() => { setMonthFrom(monthOptions[2]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 3 meses</button>
              <button onClick={() => { setMonthFrom(monthOptions[5]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 6 meses</button>
              <button onClick={() => { setMonthFrom(monthOptions[11]?.value || monthOptions[0].value); setMonthTo(monthOptions[0].value); }} className="text-[10px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-600">Últimos 12 meses</button>
            </div>
          </div>
        )}

        {preset === "dates" && (
          <div className="flex items-end gap-3 pt-2 border-t border-slate-100" data-testid="cro-perf-dates-row">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Desde</div>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs" data-testid="cro-perf-custom-from" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Hasta</div>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs" data-testid="cro-perf-custom-to" />
            </div>
          </div>
        )}

        {data && range && (
          <div className="text-[10px] font-mono text-slate-500">
            {platformLabel} · {range.from} → {range.to}
          </div>
        )}
      </div>

      {!enabled && (
        <div className="text-xs text-slate-400 italic">Elegí un rango de fechas válido para ver datos.</div>
      )}

      {/* Google Ads → rich direct-API view (replaces the old Metricool flow). */}
      {network === "adwords" && enabled && (
        <GoogleAdsFullSection customerId={customerId} range={range} />
      )}

      {network !== "adwords" && enabled && isLoading && (
        <div className="space-y-3">
          <div className="h-24 rounded-md skeleton" />
          <div className="h-48 rounded-md skeleton" />
        </div>
      )}

      {network !== "adwords" && data && !data.ads_connected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-5 text-xs text-amber-900 flex items-start gap-3" data-testid="cro-perf-not-connected">
          <Plug className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Cuenta de {platformLabel} no conectada en Metricool.</div>
            <p className="leading-relaxed">{data.message || `Conectá ${platformLabel} desde Metricool (sección Advertising) para que aparezcan automáticamente impresiones, clicks, conversiones, campañas y keywords aquí.`}</p>
            <p className="mt-2 text-[11px] text-amber-700">El endpoint está listo: en cuanto Metricool habilite los datos para este cliente, aparecen sin tocar nada.</p>
          </div>
        </div>
      )}

      {network !== "adwords" && data && data.ads_connected && <GoogleAdsContent data={data} network={network} />}
    </div>
  );
}

function GoogleAdsContent({ data, network }) {
  const t = data.totals || {};
  const tl = data.timelines || {};
  return (
    <div className="space-y-6">
      {/* Bloque 1 · Alcance */}
      <SectionBlock title="Alcance" subtitle="Visibilidad e inversión en el período">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Kpi icon={Eye} label="Impresiones" value={fmtNum(t.impressions)} tone="indigo" />
          <Kpi icon={DollarSign} label="Gasto" value={fmtCur(t.cost)} tone="amber" />
        </div>
        <Sparkline points={tl.impressions} color="#4f46e5" label="Impresiones / día" />
      </SectionBlock>

      {/* Bloque 2 · Resultados */}
      <SectionBlock title="Resultados" subtitle="Interacciones y conversiones generadas">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi icon={MousePointerClick} label="Clicks" value={fmtNum(t.clicks)} tone="sky" />
          <Kpi icon={Target} label="Conversiones" value={fmtNum(t.conversions)} tone="emerald" />
          <Kpi icon={DollarSign} label="Gasto" value={fmtCur(t.cost)} tone="amber" />
        </div>
        <Sparkline points={tl.clicks} color="#10b981" label="Clicks / día" />
      </SectionBlock>

      {/* Bloque 3 · Rendimiento */}
      <SectionBlock title="Rendimiento" subtitle="Eficiencia del gasto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi icon={Activity} label="CPM" value={fmtCur(t.cpm)} tone="violet" />
          <Kpi icon={MousePointer} label="CPC" value={fmtCur(t.cpc)} tone="sky" />
          <Kpi icon={TrendingUp} label="CTR" value={fmtPct(t.ctr)} tone="emerald" />
          <Kpi icon={DollarSign} label="Gasto" value={fmtCur(t.cost)} tone="amber" />
        </div>
        <Sparkline points={tl.cpm} color="#7c3aed" label="CPM / día" />
      </SectionBlock>

      {/* Tabla de campañas */}
      <CampaignsTable campaigns={data.campaigns || []} />

      {/* Tabla de keywords (solo Google Ads) */}
      {network === "adwords" && <KeywordsTable keywords={data.keywords || []} />}
    </div>
  );
}

function SectionBlock({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5">
      <div className="mb-3">
        <h3 className="font-display text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Sparkline({ points, color, label }) {
  if (!points || points.length === 0) return null;
  const data = points.map((p) => ({ date: p.date, value: p.value }));
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="cro-perf-spark">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" hide />
            <YAxis hide />
            <Tooltip
              contentStyle={{ fontSize: "11px", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 4 }}
              labelFormatter={(v) => (typeof v === "string" ? v.slice(0, 10) : v)}
              formatter={(val) => [typeof val === "number" ? val.toLocaleString("es-AR", { maximumFractionDigits: 2 }) : val, label]}
            />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CampaignsTable({ campaigns }) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("impressions");
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    const filt = campaigns.filter((c) => !search.trim() || (c.name || "").toLowerCase().includes(search.toLowerCase()));
    return [...filt].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [campaigns, search, sortKey, sortDir]);
  const onSort = (k) => { if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("desc"); } };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="cro-perf-campaigns">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Lista de campañas</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Métricas por campaña en el período seleccionado</p>
        </div>
        <div className="relative w-full sm:w-64">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Buscar campaña…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="cro-perf-camp-search" />
        </div>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
              <Th k="name" onSort={onSort} sk={sortKey} sd={sortDir}>Campaña</Th>
              <Th k="updated" onSort={onSort} sk={sortKey} sd={sortDir}>Actualizado</Th>
              <Th k="impressions" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Impr.</Th>
              <Th k="clicks" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Clicks</Th>
              <Th k="conversions" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Conv.</Th>
              <Th k="cpm" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CPM</Th>
              <Th k="cpc" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CPC</Th>
              <Th k="ctr" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CTR</Th>
              <Th k="conversion_value" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Valor conv.</Th>
              <Th k="roas" onSort={onSort} sk={sortKey} sd={sortDir} align="right">ROAS</Th>
              <Th k="spent" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Gasto</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((c) => (
              <tr key={c.provider_campaign_id || c.name} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                <td className="px-2 py-2 text-slate-500 text-[11px]">{fmtDate(c.updated)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtNum(c.impressions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700">{fmtNum(c.clicks)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-700 font-semibold">{fmtNum(c.conversions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtCur(c.cpm)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtCur(c.cpc)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtPct(c.ctr)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtCur(c.conversion_value)}</td>
                <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${(c.roas || 0) >= 3 ? "text-emerald-700" : (c.roas || 0) >= 2 ? "text-amber-700" : (c.roas || 0) > 0 ? "text-rose-700" : "text-slate-400"}`}>
                  {c.roas > 0 ? `${c.roas.toFixed(2)}×` : "—"}
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-amber-700">{fmtCur(c.spent)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={11} className="text-center text-[11px] text-slate-400 italic py-6">Sin campañas en el período.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function KeywordsTable({ keywords }) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("impressions");
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    const filt = keywords.filter((k) => !search.trim() || (k.keyword || "").toLowerCase().includes(search.toLowerCase()));
    return [...filt].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [keywords, search, sortKey, sortDir]);
  const onSort = (k) => { if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("desc"); } };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="cro-perf-keywords">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Palabras clave</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Performance por keyword en el período</p>
        </div>
        <div className="relative w-full sm:w-64">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Buscar keyword…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="cro-perf-kw-search" />
        </div>
      </div>
      <div className="overflow-x-auto -mx-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
              <Th k="keyword" onSort={onSort} sk={sortKey} sd={sortDir}>Keyword</Th>
              <Th k="campaign_name" onSort={onSort} sk={sortKey} sd={sortDir}>Campaña</Th>
              <Th k="match_type" onSort={onSort} sk={sortKey} sd={sortDir}>Match</Th>
              <Th k="impressions" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Impr.</Th>
              <Th k="clicks" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Clicks</Th>
              <Th k="conversions" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Conv.</Th>
              <Th k="cpm" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CPM</Th>
              <Th k="cpc" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CPC</Th>
              <Th k="ctr" onSort={onSort} sk={sortKey} sd={sortDir} align="right">CTR</Th>
              <Th k="spent" onSort={onSort} sk={sortKey} sd={sortDir} align="right">Gasto</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((k, i) => (
              <tr key={`${k.keyword}-${k.ad_group_name || ""}-${i}`} className={`hover:bg-slate-50 ${k.conversions === 0 && k.clicks > 30 ? "bg-rose-50/30" : ""}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{k.keyword}</td>
                <td className="px-2 py-2 text-slate-500 text-[11px]">{k.campaign_name || "—"}</td>
                <td className="px-2 py-2"><span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">{k.match_type || "—"}</span></td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{fmtNum(k.impressions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{fmtNum(k.clicks)}</td>
                <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${k.conversions > 0 ? "text-emerald-700" : "text-slate-400"}`}>{fmtNum(k.conversions)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtCur(k.cpm)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtCur(k.cpc)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{fmtPct(k.ctr)}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-amber-700">{fmtCur(k.spent)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={10} className="text-center text-[11px] text-slate-400 italic py-6">Sin keywords en el período.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 mt-3 italic">Las filas en rosa son keywords con clicks pero 0 conversiones — candidatas a keyword negativa.</p>
    </section>
  );
}

function Th({ k, sk, sd, onSort, align, children }) {
  const active = sk === k;
  return (
    <th
      onClick={() => onSort(k)}
      className={`px-2 py-2 font-semibold cursor-pointer select-none ${align === "right" ? "text-right" : ""} ${active ? "text-slate-900" : "hover:text-slate-700"}`}
    >
      {children}{active ? (sd === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

const numFmt = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 });
const curFmt = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateFmt = new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", year: "2-digit" });
function fmtNum(n) { return n == null ? "—" : numFmt.format(Math.round(n)); }
function fmtCur(n) { return n == null ? "—" : "$" + curFmt.format(n); }
function fmtPct(n) { return n == null ? "—" : `${Number(n).toFixed(2)}%`; }
function fmtDate(d) { if (!d) return "—"; try { return dateFmt.format(new Date(d)); } catch { return String(d).slice(0, 10); } }

function Kpi({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    violet: "from-violet-50 to-white border-violet-200 text-violet-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  return (
    <div className={`bg-gradient-to-b ${tones[tone]} border rounded-md p-4`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[9px] uppercase tracking-wider opacity-90 font-semibold">{label}</div>
      </div>
      <div className="font-mono text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

/* ============== Términos de búsqueda ============== */
function SearchTermsSection({ terms }) {
  const [search, setSearch] = useState("");
  const filtered = terms.filter((t) => !search.trim() || t.query.toLowerCase().includes(search.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => b.clicks - a.clicks);
  return (
    <div className="space-y-4" data-testid="cro-terms">
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-slate-900">Términos de búsqueda · Queries reales</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Lo que realmente buscaron los usuarios antes de hacer clic</p>
          </div>
          <div className="relative w-full sm:w-64">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Buscar query…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" />
          </div>
        </div>
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2 font-semibold">Query</th>
                <th className="px-2 py-2 font-semibold">Tipo match</th>
                <th className="px-2 py-2 font-semibold">Campaña</th>
                <th className="px-2 py-2 font-semibold text-right">Impr.</th>
                <th className="px-2 py-2 font-semibold text-right">Clicks</th>
                <th className="px-2 py-2 font-semibold text-right">CTR</th>
                <th className="px-2 py-2 font-semibold text-right">CPC</th>
                <th className="px-2 py-2 font-semibold text-right">Conv.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((t) => (
                <tr key={t.query} className={`hover:bg-slate-50 ${t.conversions === 0 && t.clicks > 30 ? "bg-rose-50/30" : ""}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{t.query}</td>
                  <td className="px-2 py-2"><span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">{t.match_type}</span></td>
                  <td className="px-2 py-2 text-slate-500 text-[10px]">{t.campaign}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-700">{t.impressions.toLocaleString()}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-sky-700 font-semibold">{t.clicks}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{t.ctr}%</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">${t.cpc}</td>
                  <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${t.conversions > 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.conversions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-3 italic">Las filas en rosa son queries con muchos clicks pero 0 conversiones — candidatos a keyword negativa.</p>
      </section>
    </div>
  );
}

/* ============== Acciones ============== */
function ActionsSection({ clientId }) {
  return (
    <div className="space-y-5" data-testid="cro-actions">
      <WeeklyLogSection clientId={clientId} />
    </div>
  );
}

function WeeklyLogSection({ clientId }) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["cro-weekly", clientId],
    queryFn: () => fetchCroWeekly(clientId),
    staleTime: 5 * 60 * 1000,
  });
  const entries = data?.entries || [];
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="cro-weekly-log">
      <div className="flex items-start gap-2 mb-4 flex-wrap">
        <Calendar className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Bitácora semanal · acciones del equipo</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Cambios y observaciones cargadas semana a semana en la planilla compartida</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} data-testid="cro-weekly-refresh"
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-400 px-2.5 py-1 rounded-sm transition-colors">
          <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} /> Refrescar
        </button>
        <span className="text-[10px] font-mono text-slate-400">{data?.total_entries ?? 0} entradas</span>
      </div>
      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 skeleton rounded-md" />)}</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-slate-400 italic text-center py-6 bg-slate-50 border border-dashed border-slate-200 rounded-md">
          Sin registros de este cliente en la planilla aún. Si ya estás cargando datos, podés tardar 1-2 min en aparecer (cache).
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`cro-weekly-${i}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {e.period && <span className="text-[10px] font-mono uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-sm font-semibold">{e.period}</span>}
                {e.investment && <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm font-semibold ml-auto">{e.investment}</span>}
              </div>
              {e.summary && (
                <div className="mb-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Resumen / Resultados</div>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{e.summary}</p>
                </div>
              )}
              {e.observations && (
                <div className="mb-2">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Observaciones de control</div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{e.observations}</p>
                </div>
              )}
              {e.pending && (
                <div className="mt-2 pt-2 border-t border-slate-200 flex items-start gap-1.5">
                  <FileText className="h-3 w-3 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 leading-relaxed flex-1"><span className="font-semibold">Pendientes:</span> {e.pending}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyTab({ msg }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="cro-tab-empty">
      <MousePointer className="h-7 w-7 text-slate-400 mx-auto mb-2" />
      <p className="text-sm text-slate-500">{msg}</p>
    </div>
  );
}
